import * as React from 'react'
import { useEffect, useRef } from 'react'
import type { JSX } from 'react'
import type { MouseEventHandler } from 'react'

import { useSplit } from '@buffer-mono/features'
import { Button, Flex, Notice } from '@buffer-mono/popcorn'

import { useRegisteredBanners } from '~publish/components/RegisteredBanner'
import { useCanStartTrial } from '~publish/contexts/AccountContext'
import { useCta } from '~publish/hooks/useCta'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import useQueueLimitBar from '~publish/hooks/useQueueLimitBar'
import { SEGMENT_NAMES } from '~publish/legacy/constants'

import styles from './QueueLimitNotice.module.css'

const CTA: CTA = SEGMENT_NAMES.QUEUE_LIMIT_BANNER_UPGRADE
const UPGRADE_PATH_NAME = 'queueLimit-upgrade'
const UPGRADE_PATH_NAME_TRIAL = 'queueLimit-trial'

export const QueueLimitNotice = (): JSX.Element | null => {
  const { numberOfScheduledPosts, postLimit, shouldShow } = useQueueLimitBar()

  const canStartTrial = useCanStartTrial()
  const openGlobalModal = useOpenGlobalModal()
  const { split: featureTrialSplit, isReady } = useSplit(
    'geid-feature-specific-trial',
  )
  const isInFeatureTrialSplit = isReady && featureTrialSplit?.treatment === 'on'

  const cta = useCta(CTA, {
    upgradePathName:
      canStartTrial && isInFeatureTrialSplit
        ? UPGRADE_PATH_NAME_TRIAL
        : UPGRADE_PATH_NAME,
  })

  // Track the upgrade path view for the queue limit notice
  // Only track once when the notice first becomes visible
  const hasTrackedRef = useRef(false)
  useEffect(() => {
    if (shouldShow && !hasTrackedRef.current) {
      cta.viewed()
      hasTrackedRef.current = true
    }
  }, [shouldShow, cta])

  // Suppress banners if this notice is visible
  const { suppressBanners, unsuppressBanners } = useRegisteredBanners()
  React.useLayoutEffect(() => {
    if (shouldShow) {
      suppressBanners()
    }
    return (): void => unsuppressBanners()
  }, [shouldShow, suppressBanners, unsuppressBanners])

  if (!shouldShow) {
    return null
  }

  const handleUpgradeClick: MouseEventHandler<HTMLButtonElement> = (): void => {
    cta.clicked()

    if (canStartTrial && isInFeatureTrialSplit) {
      openGlobalModal(MODALS.featureTrial, {
        feature: 'queueLimit',
        cta: CTA,
        upgradePathName: UPGRADE_PATH_NAME_TRIAL,
      })
    } else {
      openGlobalModal(MODALS.planSelector, {
        cta: CTA,
        upgradePathName: UPGRADE_PATH_NAME,
      })
    }
  }

  const postsRemaining = postLimit - numberOfScheduledPosts
  const reachedLimit = numberOfScheduledPosts >= postLimit
  const variant = reachedLimit ? 'upgrade' : 'info'
  const buttonVariant = reachedLimit ? 'upgrade' : 'primary'

  const headingCopy = `${postsRemaining <= 0 ? 0 : postsRemaining} ${
    postsRemaining === 1 ? 'Post' : 'Posts'
  } left to schedule on the Free plan.`

  // Change button copy based on whether user can start trial and is in split
  const buttonCopy =
    canStartTrial && isInFeatureTrialSplit
      ? 'Start trial for more posts'
      : 'Upgrade for Unlimited Posts'

  return (
    <Notice
      data-testid="queue-limit-notice"
      variant={variant}
      data-state={variant}
      className={styles.notice}
    >
      <Flex justify="between" align="center" gap="xs" fullWidth>
        <Notice.Text className={styles.text}>{headingCopy}</Notice.Text>
        <Notice.Actions>
          <Button
            variant={buttonVariant}
            size="small"
            onClick={handleUpgradeClick}
            aria-haspopup="dialog"
          >
            {buttonCopy}
          </Button>
        </Notice.Actions>
      </Flex>
    </Notice>
  )
}
